import { Menu, MenuItem, ListItem, ListItemButton, ListItemIcon, ListItemText, Tooltip, Button, Box, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { Language as LanguageIcon } from '@mui/icons-material';
import { Colors } from 'src/shared/components/styles/colors';
import { useGlobals } from 'src/services/contexts/appContext';

export interface LanguageItem {
  name: string;
  value: 'sv' | 'en' | 'helsinge';
}

export interface SwitchLanguageButtonProps {
  variant?: 'button' | 'listItem';
  collapsed?: boolean;
}

export const SwitchLanguageButton = (props: SwitchLanguageButtonProps) => {
  const { variant, collapsed } = props;
  const { setLanguage, language } = useGlobals();
  const { t } = useTranslation();
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const availableLanguages: LanguageItem[] = [
    {
      name: t('language.swedish'),
      value: 'sv',
    },
    {
      name: t('language.english'),
      value: 'en',
    },
    {
      name: t('language.helsinge'),
      value: 'helsinge',
    },
  ];
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const changeLanguage = (lng: string) => {
    setAnchorEl(null);
    setLanguage(lng);
  };

  const currentLanguageName = language ? availableLanguages.find((l) => l.value === language)?.name : '';

  const menu = (
    <Menu
      id='basic-menu'
      anchorEl={anchorEl}
      open={open}
      onClose={handleClose}
      MenuListProps={{
        'aria-labelledby': 'basic-button',
      }}
    >
      {availableLanguages.map((item) => {
        return (
          <MenuItem key={item.value} onClick={() => changeLanguage(item.value)} sx={{ fontWeight: item.value === language ? 600 : 200 }}>
            {item.name}
          </MenuItem>
        );
      })}
    </Menu>
  );

  if (variant === 'listItem') {
    return (
      <ListItem disablePadding sx={{ mb: 0.75 }}>
        <Tooltip title={collapsed ? currentLanguageName : ''} placement='right'>
          <ListItemButton
            aria-controls={open ? 'basic-menu' : undefined}
            aria-haspopup='true'
            aria-expanded={open ? 'true' : undefined}
            onClick={handleClick}
            sx={{
              minHeight: 46,
              borderRadius: 2,
              justifyContent: collapsed ? 'center' : 'flex-start',
              px: collapsed ? 1.5 : 2,
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: collapsed ? 0 : 36,
                justifyContent: 'center',
                color: Colors.BLACK,
              }}
            >
              <LanguageIcon />
            </ListItemIcon>
            {!collapsed ? <ListItemText primary={currentLanguageName} /> : null}
          </ListItemButton>
        </Tooltip>
        {menu}
      </ListItem>
    );
  }

  return (
    <>
      <Button
        sx={{
          color: isTablet ? Colors.ACCENT_DARK : Colors.BLACK,
          textTransform: 'none',
          '&:hover': {
            backgroundColor: isTablet ? 'transparent' : 'default',
          },
          padding: 0.5,
        }}
        aria-controls={open ? 'basic-menu' : undefined}
        aria-haspopup='true'
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
      >
        <LanguageIcon />
        <Box sx={{ pl: 1 }}>{currentLanguageName}</Box>
      </Button>
      {menu}
    </>
  );
};
SwitchLanguageButton.defaultProps = {
  variant: 'button',
  collapsed: false,
};
